import { Helmet } from 'react-helmet-async';
import { useState, useMemo, useRef } from 'react';
// @mui
import {
  Card,
  Container,
  Stack,
  Button,
  Box
} from '@mui/material';
import MUIDataTable from "mui-datatables";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { utcToZonedTime, format } from 'date-fns-tz';
import { id } from "date-fns/locale";
import { endOfDay, parseISO } from 'date-fns';
import { TableLoader } from '../../../components/table';
import { getCurrentDate } from "../../../utils/formatDate";
// Context
import { axiosPrivate } from '../../../common/axiosPrivate';

export default function LaporanPenagihanPetugasPage() {
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const BodyComponent = useMemo(
    () => (props) => (
      <TableLoader loading={tableLoading} {...props} />
    ),
    [tableLoading]
  );

  const columns = [
    {
      name: "number",
      label: "No",
      options: {
        filter: false,
        sort: false,
        customBodyRender : (value, tableMeta) => {
          const rowIndex = Number(tableMeta.rowIndex) + 1;
          return rowIndex;
        }
      },
    },
    {
      name: "kantor",
      label: "Kantor",
      options: {
       filter: false,
       sort: true,
      }
    },
    {
      name: "kodePegawai",
      label: "Kode Pegawai",
      options: {
       filter: false,
       sort: true,
      }
    },
    {
      name: "nama",
      label: "Petugas",
      options: {
       filter: true,
       sort: true,
      }
    },
    {
      name: "namaJabatan",
      label: "Jabatan",
      options: {
       filter: true,
       sort: true,
      }
    },
    {
      name: "jenisAbsen",
      label: "Absen",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => value || '-'
      },
    },
    {
      name: "jamMasuk",
      label: "Absen Masuk",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) =>
          value
            ? format(utcToZonedTime(parseISO(value), 'UTC'), 'HH:mm', {
                timeZone: 'UTC',
              })
            : '-',
      },
    },
    {
      name: "waktuMulai",
      label: "Waktu Mulai",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) =>
          value
            ? format(utcToZonedTime(parseISO(value), 'UTC'), 'HH:mm', {
                timeZone: 'UTC',
              })
            : '-',
      },
    },
    {
      name: "selesai",
      label: "Selesai",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) =>
          value
            ? format(utcToZonedTime(parseISO(value), 'UTC'), 'HH:mm', {
                timeZone: 'UTC',
              })
            : '-',
      },
    },
    {
      name: "totalPenagihan",
      label: "Total Penagihan",
      options: {
       filter: false,
       sort: true,
      }
    },
    {
      name: "tertagih",
      label: "Tertagih",
      options: {
       filter: false,
       sort: true,
      }
    },
    {
      name: "tidakTertagih",
      label: "Tidak Tertagih",
      options: {
       filter: false,
       sort: true,
      }
    },
    {
      name: "nominal",
      label: "Nominal",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          const number = Number(value);
          if (!value || Number.isNaN(number) || number === 0) return 0;
          return number.toLocaleString("id-ID"); // hasil: 1.000.000
        }
      },
    },
    {
      name: "totalTagihOffline",
      label: "Total Manual Absen",
      options: {
       filter: false,
       sort: true,
      }
    },
    {
      name: "totalNominalOffline",
      label: "Total Manual Nominal",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          const number = Number(value);
          if (!value || Number.isNaN(number) || number === 0) return 0;
          return number.toLocaleString("id-ID"); // hasil: 1.000.000
        }
      },
    },
    {
      name: "keteranganOffline",
      label: "Keterangan Kendala",
      options: {
       filter: false,
       sort: true,
      }
    },
    {
      name: "totalPenagihanAll",
      label: "Grand Total Penagihan",
      options: {
        filter: false,
        sort: true
      },
    },
    {
      name: "grandTotal",
      label: "Grand Total Nominal Penagihan",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          const number = Number(value);
          if (!value || Number.isNaN(number) || number === 0) return 0;
          return number.toLocaleString("id-ID"); // hasil: 1.000.000
        }
      },
    },
  ];
   
  const options = {
    filterType: "dropdown",
    selectableRowsHideCheckboxes: true,
    downloadOptions: {
      filename: `Pengihan-By-Petugas-${getCurrentDate('-')}.csv`,
      filterOptions: {
        useDisplayedColumnsOnly: false,
        useDisplayedRowsOnly: true,
      }
    },
  };
  
  const handleStartDateChange = (date) => {
    startDateRef.current = date;
  };

  const handleEndDateChange = (date) => {
    endDateRef.current = date;
  };

  const filterData = () => {
    setTableLoading(true);

    const modEndDate = endOfDay(endDateRef.current);

    axiosPrivate.post(`/laporan/monitoring-penagihan-by-petugas`, {startDate: startDateRef.current, endDate: modEndDate})
    .then((response) => {
      const data = response?.data;
      
      setData(data);
    })
    .catch((error) => console.log(error))
    .finally(() => setTableLoading(false));
  }

  return (
    <>
      <Helmet>
        <title> Laporan Penagihan By Petugas | Sip App </title>
      </Helmet>
      <Container
        sx={{
          mt: 5
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="start">
            <Box sx={{ marginRight: 2 }}>
              <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Dari Tanggal"
                  value={startDateRef.current}
                  onChange={handleStartDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ marginRight: 2 }}>
              <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Sampai Tanggal"
                  value={endDateRef.current}
                  onChange={handleEndDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Box>

            <Button variant="contained" onClick={filterData}>
              Filter
            </Button>
          </Stack>
        </Box>
        <Card>
          <MUIDataTable
            title="Laporan Penagihan By Petugas"
            data={data}
            columns={columns}
            options={options}
            components={{ TableBody: BodyComponent }}
          />
        </Card>
      </Container>
    </>
  );
}
